import NOTIFICATION_ACCOUNT from 'constants/notification/services/account';
import { first, isEmpty, isNil } from 'lodash-es';
import { AccountDetailsServiceData, EmailConfirmationResultServiceData } from 'services/AuthService/Login/loginServiceType';
import { OneMutationService, OneMutationServiceMutator, OneServiceFetcherNoParams, OneServiceNoParams } from 'types/services/services';
import { oneApi } from 'utils/api/api-fetcher';
import isHTTPError from 'utils/api/isHTTPError';
import OneBadRequest from 'utils/common/errors/OneBadRequest';
import OneBusinessError from 'utils/common/errors/OneBusinessError';
import notEmpty from 'utils/common/notEmpty';
import manageError from 'utils/logging/manageError';
import { STANDARD_CACHE_TIME, STANDARD_STALE_TIME } from 'utils/query/standardOptions';

const SERVICE_ID = 'account/details';

const getAccountFetcher: OneServiceFetcherNoParams<AccountDetailsServiceData> = async () => {
  const { data } = await oneApi.get<AccountDetailsServiceData[]>('account/details');

  const account = first(data);

  if (isNil(account)) {
    throw new Error('Getting account details failed');
  }

  return account;
};

export type UpdateAccountParams = {
  accountUpdate: Partial<
    Pick<
      AccountDetailsServiceData,
      | 'firstName'
      | 'lastName'
      | 'emailAddress'
      | 'accountType'
      | 'zipCode'
      | 'address'
      | 'streetNumber'
      | 'city'
      | 'countryCode'
      | 'phoneNumber'
      | 'phoneNumberMobile'
      | 'companyName'
      | 'vatNumber'
      | 'cocNumber'
      | 'emailAddressAbuse'
      | 'newsletter'
    >
  >;
};

// Some accounts are created has type 0 and we need to send a type
// For more info check ticket PT-567
const buildAccountType = (model: Partial<AccountDetailsServiceData>) => {
  if (model.accountType === 0) {
    return notEmpty(model.companyName) ? 2 : 1;
  }

  return model.accountType;
};

const updateAccountMutator: OneMutationServiceMutator<AccountDetailsServiceData, UpdateAccountParams> = async ({ accountUpdate }) => {
  if (isEmpty(accountUpdate)) {
    throw new Error('Unexpected empty params');
  }

  const accountType = buildAccountType(accountUpdate);

  const { data } = await oneApi.put<UpdateAccountParams['accountUpdate'], AccountDetailsServiceData[]>('account/details', {
    params: {
      ...accountUpdate,
      accountType,
    },
  });

  const account = first(data);

  if (isNil(account)) {
    throw new Error('Account update failed');
  }

  return account;
};

export type UpdateEmailParams = { emailAddress: string; password: string };

const updateEmailMutator: OneMutationServiceMutator<null, UpdateEmailParams> = async ({ emailAddress, password }) => {
  if (isNil(emailAddress) || isNil(password)) {
    throw new Error('Unexpected empty params');
  }

  try {
    await oneApi.put<UpdateEmailParams, null>('account/email/change', { params: { emailAddress, password } });

    return null;
  } catch (error) {
    if (isHTTPError(error) && error.response.status === 401) {
      throw new OneBadRequest(
        { errorCode: 401, errorMessage: 'Password incorrect', errors: [{ message: 'The password is incorrect', property: 'currentPassword' }] },
        error,
      );
    }

    if (isHTTPError(error) && error.response.status === 409) {
      throw new OneBadRequest(
        {
          errorCode: 409,
          errorMessage: 'Email conflict',
          errors: [{ message: 'Either there is an email address conflict or validate the email (check the spam inbox)', property: 'emailAddress' }],
        },
        error,
      );
    }

    throw error;
  }
};

export type ConfirmEmailParams = { userId: string; hash: string };

const confirmEmailMutator: OneMutationServiceMutator<EmailConfirmationResultServiceData, ConfirmEmailParams> = async ({ userId, hash }) => {
  if (isNil(userId) || isNil(hash)) {
    throw new Error('Unexpected empty params');
  }

  try {
    const { data } = await oneApi.put<ConfirmEmailParams, EmailConfirmationResultServiceData>('account/email/confirm', { params: { userId, hash } });

    return data;
  } catch (error) {
    if (isHTTPError(error)) {
      if (error.response.status >= 500) {
        // unexpected error
        manageError(error, { userId, hash });
      }

      throw new OneBusinessError('Invalid verification link', error);
    }

    throw error;
  }
};

const getAccount: OneServiceNoParams<AccountDetailsServiceData> = {
  id: 'get',
  serviceId: SERVICE_ID,
  fetcher: getAccountFetcher,
  options: {
    staleTime: STANDARD_STALE_TIME.long,
    cacheTime: STANDARD_CACHE_TIME.long,
    refetchOnWindowFocus: false,
  },
};

const updateAccount: OneMutationService<AccountDetailsServiceData, UpdateAccountParams> = {
  id: 'update',
  serviceId: SERVICE_ID,
  mutation: updateAccountMutator,
  options: { meta: { notifications: { success: NOTIFICATION_ACCOUNT.accountUpdateSuccess } } },
};

const updateEmail: OneMutationService<null, UpdateEmailParams> = {
  id: 'updateEmail',
  serviceId: SERVICE_ID,
  mutation: updateEmailMutator,
  options: { meta: { notifications: { success: NOTIFICATION_ACCOUNT.accountEmailUpdateSuccess } } },
};

const confirmEmail: OneMutationService<EmailConfirmationResultServiceData, ConfirmEmailParams> = {
  id: 'confirmEmail',
  serviceId: SERVICE_ID,
  mutation: confirmEmailMutator,
};

const accountService = {
  getAccount,
  updateAccount,
  updateEmail,
  confirmEmail,
};

export default accountService;
